/**
 * 「概要」タブ。ログインの様子(最新ログイン・セッション・連続失敗)、パスワード、
 * MFA(OTP)と、それぞれの運用操作(強制ログアウト / ロック解除 / OTP リセット)。
 */
import { useMutation } from "@tanstack/react-query";
import { Button, Space, message } from "antd";

import type { IdpUserDetail } from "../../../lib/api";
import { jst } from "../../../lib/format";
import { useApi } from "../../../lib/useApi";
import { KeyValueList, Section, StatusTag, TimeAgo } from "../../../ui";
import { errorText, useInvalidateIdp } from "../hooks";

export function OverviewTab({ user, canAdmin }: { user: IdpUserDetail; canAdmin: boolean }) {
  const api = useApi();
  const invalidate = useInvalidateIdp();
  const id = user.id;

  /** ボタン1つぶんの操作。成功したら一覧と詳細を取り直す。 */
  const done = (label: string) => ({
    onSuccess: () => {
      invalidate();
      void message.success(`${label}した`);
    },
    onError: (e: unknown) => void message.error(errorText(e)),
  });
  const logout = useMutation({ mutationFn: () => api.logoutIdpUser(id), ...done("強制ログアウト") });
  const unlock = useMutation({ mutationFn: () => api.unlockIdpUser(id), ...done("ロック解除") });
  const otpReset = useMutation({ mutationFn: () => api.resetIdpOtp(id), ...done("OTP をリセット") });

  const info = user.accessInfo;
  return (
    <div className="ui-detail-grid">
      <div className="ui-detail-col">
        <Section
          title="ログイン"
          extra={
            canAdmin && (
              <Space>
                <Button size="small" loading={logout.isPending} onClick={() => logout.mutate()}>
                  強制ログアウト
                </Button>
                <Button
                  size="small"
                  loading={unlock.isPending}
                  disabled={!info.locked && info.failureCount === 0}
                  onClick={() => unlock.mutate()}
                >
                  ロック解除
                </Button>
              </Space>
            )
          }
        >
          <KeyValueList
            items={[
              { label: "最新ログイン", always: true, value: info.lastLogin ? <TimeAgo iso={info.lastLogin} /> : "" },
              { label: "いまのセッション", always: true, value: `${info.sessions} 件` },
              {
                label: "連続失敗",
                always: true,
                value: (
                  <Space size={8}>
                    <span>{info.failureCount} 回</span>
                    {info.locked && <StatusTag tone="warning">一時ロック中</StatusTag>}
                  </Space>
                ),
              },
              {
                label: "最後の失敗",
                value: info.lastFailureAt
                  ? `${jst(info.lastFailureAt)}${info.lastFailureIp ? `(${info.lastFailureIp})` : ""}`
                  : "",
              },
            ]}
            unsetLabel="なし"
          />
        </Section>
      </div>
      <div className="ui-detail-col">
        <Section title="パスワード">
          <KeyValueList
            items={[
              { label: "認証方法", value: user.password.method },
              { label: "最終更新", value: jst(user.password.updatedAt) },
              {
                label: "有効期限",
                always: true,
                value: user.password.expiresAt
                  ? `${jst(user.password.expiresAt)}(ポリシー ${user.password.expiryDays} 日)`
                  : "無期限(ポリシー未設定)",
              },
            ]}
          />
        </Section>
        <Section
          title="MFA(OTP)"
          extra={
            canAdmin && (
              <Button
                size="small"
                loading={otpReset.isPending}
                disabled={!user.otp.registered}
                onClick={() => otpReset.mutate()}
              >
                OTP をリセット
              </Button>
            )
          }
        >
          <KeyValueList
            items={[
              {
                label: "登録",
                always: true,
                value: user.otp.registered ? (
                  <StatusTag tone="success">あり({user.otp.count} 件)</StatusTag>
                ) : (
                  <StatusTag tone="neutral">なし</StatusTag>
                ),
              },
              { label: "登録日時", value: jst(user.otp.registeredAt) },
            ]}
          />
          <div style={{ marginTop: 8, color: "var(--ux-text-tertiary)", fontSize: 12 }}>
            リセットすると本人が次のログインで登録し直す。
          </div>
        </Section>
      </div>
    </div>
  );
}
